<script setup lang="ts">
import {ref, computed, onMounted} from 'vue';
import {AllSpiceJsonLoader} from '../features/allspicejson.ts';
import {globalContainer} from '../container.ts';
import type {ProjectData} from '../allspice_app/types/project-data.ts';

type VariantInfo = {
  id: string;
  name: string;
};

const props = defineProps<{
  owner: string;
  repo: string;
  headCommit: string;
  baseCommit: string;
  savedVariant: VariantInfo | null;
  disabled: boolean;
  locale: {
    variantHeader: string;
    noVariantsNotice: string;
    defaultLabel: string;
  };
}>();

const emit = defineEmits<{
  variantChange: [variant: VariantInfo | null];
}>();

const DEFAULT_ID = 'default';

const variants = ref<VariantInfo[]>([]);
const selectedId = ref(DEFAULT_ID);
const isLoading = ref(false);
const loaded = ref(false);

const noVariants = computed(() => loaded.value && variants.value.length <= 1);

onMounted(() => {
  loadVariants();
});

function emitSelection() {
  if (selectedId.value === DEFAULT_ID) {
    emit('variantChange', null);
  } else {
    const variant = variants.value.find((v) => v.id === selectedId.value);
    emit('variantChange', variant ?? null);
  }
}

function onSelect() {
  emitSelection();
}

async function loadVariants() {
  isLoading.value = true;
  try {
    const url = AllSpiceJsonLoader.buildPrjRangeUrl(
      globalContainer, props.owner, props.repo, props.baseCommit, props.headCommit,
    );
    const results = await AllSpiceJsonLoader.load<ProjectData>(globalContainer, url, null);
    const variantMap: Record<string, string> = results.headJson?.variants || {};

    const userVariants: VariantInfo[] = Object.entries(variantMap)
      .map(([id, name]) => ({id, name}))
      .sort((a, b) => a.name.localeCompare(b.name, 'en', {numeric: true}));

    variants.value = [
      {id: DEFAULT_ID, name: props.locale.defaultLabel},
      ...userVariants,
    ];

    if (props.savedVariant && userVariants.some((v) => v.id === props.savedVariant!.id)) {
      selectedId.value = props.savedVariant.id;
    } else {
      selectedId.value = DEFAULT_ID;
    }

    loaded.value = true;
    emitSelection();
  } catch {
    loaded.value = true;
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="tw:mt-3">
    <div class="tw:font-semibold tw:mb-2">{{ locale.variantHeader }}</div>
    <select
      class="ui selection dropdown tw:w-full"
      :disabled="disabled || noVariants || isLoading"
      v-model="selectedId"
      @change="onSelect"
    >
      <option v-for="v in variants" :key="v.id" :value="v.id">
        {{ v.name }}
      </option>
    </select>
    <div v-if="noVariants && !isLoading" class="ui info message tw:text-sm tw:mt-1 tw:mb-0">
      {{ locale.noVariantsNotice }}
    </div>
  </div>
</template>
